import { detailInfoStore } from "@/routes/org/_components/modal/detail-info-store"
import DetailGrid from "../../../../components/detail-modal/detail-grid"
import { useEffect, useState } from "react"
import LeakChart from "./leak-chart"
import { leakChartStore } from "@/routes/org/_components/modal/leak-chart-store"
import Flex from "../../../../components/flex"
import { calcStartDate } from "@/utils/calc-start-date-util"
import { langStore } from "@/stores/lang-store"
import CloseIcon from "../../../../components/icons/close-icon"
import { periodStore } from "@/components/detail-modal/period-store"
import PeriodSettingDesktop from "../../../../components/detail-modal/period-setting-desktop"
import PeriodSettingMobile from "../../../../components/detail-modal/period-setting-mobile"
import LeakHeatmapCalendar from "../../../../components/detail-modal/leak-heatmap-calendar"
import LeakMonthDetail from "../../../../components/detail-modal/leak-month-detail"
import { useParams } from "react-router-dom"
import { modalBundle } from "../../../../components/detail-modal/detail-modal-const"
import Wrapper from "@/components/wrapper"
import { leakChartBundle } from "@/routes/apt/[apt_code]/analysis/_components/leak/leak-chart-const"

const LeakDetailModal = () => {
  const { lang } = langStore()
  const { org } = useParams()

  const FIXED_END_DATE =
    org === "seoul"
      ? "2025-11-17"
      : org === "daegu"
        ? "2026-01-26"
        : org === "jangheung"
          ? "2025-08-27"
          : org === "cubiq"
            ? "2025-11-01"
            : new Date().toISOString().slice(0, 10)

  const { isOpen, selectedRow, closeModal } = detailInfoStore()

  // ! 사업소, 중블럭은 optional이기 때문에 키를 가지고 잇는 경우에만 사용한다.
  const filteredUserRows = modalBundle.userHeader.rows.filter(
    (r) =>
      selectedRow?.[r.key] !== undefined &&
      selectedRow?.[r.key] !== null &&
      selectedRow?.[r.key] !== ""
  )

  const { usageSum, startLabel, endLabel, leakSum, leakRate, getLeakRaw, series, xLabels } =
    leakChartStore()

  const { startDate, endDate, setStartDate, setEndDate, setActive } = periodStore()
  const [selectedMonthDate, setSelectedMonthDate] = useState(null)

  useEffect(() => {
    if (!selectedRow) return

    // 모바일 캘린더용 고정 시작일 (7월 1일)
    // const start_date = "2025-08-01" // cubiq
    // const start_date = "2025-05-01" // cubiq
    const start_date = calcStartDate(FIXED_END_DATE, "3months")

    setActive("3months")
    setStartDate(start_date)
    setEndDate(FIXED_END_DATE)
    getLeakRaw(org, { key: selectedRow["customer_id"], start_date, end_date: FIXED_END_DATE })
  }, [isOpen, selectedRow, FIXED_END_DATE, getLeakRaw, org, setActive, setEndDate, setStartDate])

  const onPeriodClick = async (preset) => {
    const start_date = calcStartDate(FIXED_END_DATE, preset)
    setActive(preset)
    setStartDate(start_date)
    setEndDate(FIXED_END_DATE)
    await getLeakRaw(org, { key: selectedRow["customer_id"], start_date, end_date: FIXED_END_DATE })
  }

  const onPeriodSearch = async () => {
    await getLeakRaw(org, {
      key: selectedRow["customer_id"],
      start_date: startDate,
      end_date: endDate
    })
  }

  const getDailyMap = () => {
    const leakSeries = series.find((s) => s.name?.includes("누수")) || series[1] || series[0]
    const usageSeries = series.find((s) => s.name?.includes("사용")) || series[0]

    const leakValues = leakSeries?.data || []
    const usageValues = usageSeries?.data || []

    const dailyMap = {}

    xLabels.forEach((label, index) => {
      const date = label.substring(0, 10)

      if (!dailyMap[date]) {
        dailyMap[date] = {
          leak: 0,
          usage: 0
        }
      }

      dailyMap[date].leak += Number(leakValues[index]) || 0
      dailyMap[date].usage += Number(usageValues[index]) || 0
    })

    const dailyData = {}
    let maxDailyRate = 0

    Object.entries(dailyMap).forEach(([date, { leak, usage }]) => {
      const rate = usage > 0 ? (leak / usage) * 100 : leak > 0 ? 100 : 0

      dailyData[date] = rate
      maxDailyRate = Math.max(maxDailyRate, rate)
    })

    return {
      dailyData,
      maxDailyRate
    }
  }

  const { dailyData, maxDailyRate } = getDailyMap()
  const handleMonthClick = (date) => {
    setSelectedMonthDate(date)
  }

  const handleBack = () => {
    setSelectedMonthDate(null)
  }

  if (!isOpen || !selectedRow) return null
  if (!series) return null

  const leakPoint = `${selectedRow["leak_hours"]} (${selectedRow["leak_start_at"].slice(0, 10)} ~ ${selectedRow["leak_end_at"].slice(0, 10)})`
  const leakInfoRow = {
    ...selectedRow,
    leak_hours: leakPoint,
    leak_severity: selectedRow["leak_severity"][lang]
  }

  return (
    <Wrapper.modal
      className="md:rounded-xl w-full h-full md:h-auto md:min-w-2xl md:max-w-6xl"
      onClose={closeModal}
    >
      {/* [PC 화면 영역] */}
      <div className="hidden md:block p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">{modalBundle.title[lang]}</h2>
          <button
            className="px-2 py-1 rounded-md bg-gray-200 hover:bg-gray-300 text-base"
            onClick={closeModal}
          >
            <CloseIcon />
          </button>
        </div>

        <DetailGrid
          title={modalBundle.userHeader.title[lang]}
          headers={filteredUserRows}
          data={selectedRow}
        />
        <DetailGrid
          title={modalBundle.infoHeader.title[lang]}
          headers={modalBundle.infoHeader.rows}
          data={leakInfoRow}
        />

        <div className="mt-6">
          <h3 className="font-semibold text-lg text-left mb-2">{leakChartBundle.title[lang]}</h3>
          <PeriodSettingDesktop
            bundle={leakChartBundle}
            onPeriodClick={onPeriodClick}
            onPeriodSearch={onPeriodSearch}
          />
          <article className="px-2 py-4 border-t-2">
            <LeakChart xLabels={xLabels} series={series} />

            <Flex gap={24} className="flex-row justify-center mt-6">
              <p className="min-w-60">
                <span className="mr-2 text-gray-500">{leakChartBundle.info.period[lang]} :</span>
                {startLabel}
                {leakChartBundle.time[lang]} ~ {endLabel}
                {leakChartBundle.time[lang]}
              </p>
              <p className="min-w-35 text-center">
                <span className="mr-2 text-gray-500">{leakChartBundle.info.usage[lang]} :</span>
                {usageSum.toFixed(2)} m³
              </p>
              <p className="min-w-35 text-center">
                <span className="mr-2 text-gray-500">
                  {leakChartBundle.info.leakAmount[lang]} :
                </span>
                {leakSum.toFixed(2)} m³
              </p>
              <p className="min-w-35 text-center">
                <span className="mr-2 text-gray-500">{leakChartBundle.info.leakRate[lang]} :</span>
                {leakRate.toFixed(2)} %
              </p>
            </Flex>
          </article>
        </div>
      </div>

      {/* [모바일 화면 영역] */}
      <div className="block md:hidden w-full h-full relative overflow-hidden bg-white">
        <div
          className={`
              flex w-[200%] h-full transition-transform duration-300 ease-in-out
              ${selectedMonthDate ? "-translate-x-1/2" : "translate-x-0"}
            `}
        >
          {/* [Slide 1] 메인 상세 정보 화면 (왼쪽) */}
          <div className="w-1/2 h-full overflow-y-auto p-4">
            {/* 모바일 헤더 & 닫기 */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{selectedRow["customer_id"]}</h2>
              <button className="p-2 bg-gray-100 rounded-md" onClick={closeModal}>
                <CloseIcon />
              </button>
            </div>

            <DetailGrid
              title={modalBundle.infoHeader.title[lang]}
              headers={modalBundle.infoHeader.rows}
              data={leakInfoRow}
            />

            <div className="mt-6 pb-10">
              <h3 className="font-semibold text-lg text-left mb-2">
                {leakChartBundle.title[lang]}
              </h3>
              <PeriodSettingMobile bundle={leakChartBundle} onPeriodSearch={onPeriodSearch} />

              {/* 히트맵 캘린더 */}
              <div className="min-h-60 mb-6">
                <LeakHeatmapCalendar
                  startDate={startDate}
                  endDate={endDate}
                  dailyData={dailyData}
                  maxVal={maxDailyRate}
                  onMonthClick={handleMonthClick}
                />
              </div>
            </div>
          </div>

          {/* [Slide 2] 월별 상세 화면 (오른쪽) */}
          <div className="w-1/2 h-full overflow-y-auto p-4 bg-white">
            <LeakMonthDetail
              date={selectedMonthDate}
              onBack={handleBack}
              dailyData={dailyData}
              maxVal={maxDailyRate}
              series={series}
              xLabels={xLabels}
            />
          </div>
        </div>
      </div>
    </Wrapper.modal>
  )
}

export default LeakDetailModal
